import React, { useEffect, useState } from 'react';
import { Table } from 'antd';
import type { TableColumnsType } from 'antd';
import { Button } from 'antd';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import Checkbox from 'antd/lib/checkbox';
import { getQuestionnaireVersionLabel } from './questionnaire-version';

interface Props {
    jobId: number;
    submissions: any[];
    taskId: number;
    users: any[];
}

function JobSublist(props: Props) {
    interface ColumnType {
        key: React.Key;
        job: any;
        assignee: string;
        phase: string;
        time: string;
        questionnaireVersion: string;
    }

    const history = useHistory();
    const [data, setData] = useState<ColumnType[]>([]);
    const [duplicateCheckBox, setDuplicateCheckBox] = useState(false);
    const [duplicateSubmissions, setDuplicateSubmissions] = useState(false);
    const columns: TableColumnsType<ColumnType> = [
        {
            title: 'Job',
            dataIndex: 'job',
            key: 'job',
            render(job: any): JSX.Element {
                return (
                    <div>
                        <Button
                            className='cvat-open-job-button'
                            type='link'
                            onClick={(e: React.MouseEvent): void => {
                                e.preventDefault();
                                history.push(`/tasks/${job.taskId}/jobs/${job.jobId}`);
                            }}
                            href={`/tasks/${job.taskId}/jobs/${job.jobId}`}
                        >
                            {`#${job.jobId}`}
                        </Button>
                    </div>
                );
            },
        },
        {
            title: 'Assignee',
            dataIndex: 'assignee',
            key: 'assignee',
        },
        {
            title: 'Phase',
            dataIndex: 'phase',
            key: 'phase',
            filters: [
                { text: 'phase0', value: 'phase0' },
                { text: 'phase1a', value: 'phase1a' },
                { text: 'phase1b', value: 'phase1b' },
                { text: 'phase2a', value: 'phase2a' },
                { text: 'phase2b', value: 'phase2b' },
                { text: 'phase3a', value: 'phase3a' },
                { text: 'phase3b', value: 'phase3b' },
                { text: 'phase4a', value: 'phase4a' },
                { text: 'phase4b', value: 'phase4b' },
            ],
            onFilter: (value: string | number | boolean, record: any) => record.phase === value,
        },
        {
            title: 'Submission Time',
            dataIndex: 'time',
            key: 'time',
            render(time: string): JSX.Element {
                const [date, timestamp] = time.split('T');
                return (
                    <div>
                        {date} {timestamp.slice(0, 5)}
                    </div>
                );
            },
            sorter: (a: ColumnType, b: ColumnType) => moment(a.time).unix() - moment(b.time).unix(),
            defaultSortOrder: 'descend',
        },
        {
            title: 'Questionnaire Version',
            dataIndex: 'questionnaireVersion',
            key: 'questionnaireVersion',
        },
    ];

    useEffect(() => {
        const submissions = props.submissions;
        const data: ColumnType[] = [];
        let filteredSubmissions = submissions.filter((submission: any) => submission.job_id === props.jobId);
        const oldCount = filteredSubmissions.length;
        if (!duplicateSubmissions) {
            const uniqueSubmissionsMap = new Map();
            filteredSubmissions.forEach((submission) => {
                const key = `${submission.job_id}-${submission.phase}`;
                const existingSubmission = uniqueSubmissionsMap.get(key);
                if (!existingSubmission || new Date(submission.time) > new Date(existingSubmission.time)) {
                    uniqueSubmissionsMap.set(key, submission);
                }
            });
            filteredSubmissions = Array.from(uniqueSubmissionsMap.values());
            const newCount = filteredSubmissions.length;
            if (oldCount !== newCount) {
                setDuplicateCheckBox(true);
            }
        }
        filteredSubmissions.forEach((submission: any) => {
            const { job_id, user_id, phase, time, questionnaire_version } = submission;
            const assignee = props.users.find((user) => user.id === user_id)?.username || '';
            const job = { jobId: job_id, taskId: props.taskId };
            data.push({
                key: job_id,
                job,
                assignee: assignee,
                phase,
                time,
                questionnaireVersion: getQuestionnaireVersionLabel(questionnaire_version),
            });
        });
        setData(data);
    }, [duplicateSubmissions, props.submissions, props.users]);
    return (
        <div>
            {duplicateCheckBox && (
                <Checkbox
                    onChange={(e) => {
                        setDuplicateSubmissions(e.target.checked);
                    }}
                    checked={duplicateSubmissions}
                >
                    Show Duplicate Submissions
                </Checkbox>
            )}
            <Table
                className='cvat-task-jobs-table'
                rowClassName={() => 'cvat-task-jobs-table-row'}
                columns={columns}
                dataSource={data}
                size='small'
                pagination={false}
            />
        </div>
    );
}

export default React.memo(JobSublist);
